import { useState, useEffect, useCallback } from 'react';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/_default/contexts/use-tenant-context';
import {
  trpcConsumerAddToCart,
  trpcConsumerGetWebsiteCart,
  trpcConsumerGetShopConfigurationOpen,
} from '@trashlab/api/src/generated/hooks';

export const useShoppingCart = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { website } = useTenantContext();
  const organizationId = website?.organization.id || '';
  const addToCartMutation = trpcConsumerAddToCart.useMutation();

  const { data: shopConfiguration } =
    trpcConsumerGetShopConfigurationOpen.useQuery({
      tenantId: organizationId,
    });

  const existingCartId =
    typeof window !== 'undefined'
      ? localStorage.getItem('currentCartId')
      : null;

  const { data: existingCart } = trpcConsumerGetWebsiteCart.useQuery(
    {
      id: existingCartId ?? '',
    },
    {
      enabled: !!existingCartId,
      retry: false,
    },
  );

  // Clear cart from localStorage if it doesn't exist or is in final step
  useEffect(() => {
    if (
      existingCartId &&
      (!existingCart ||
        existingCart.cart.currentStepKey === 'tl-site-confirmation')
    ) {
      localStorage.removeItem('currentCartId');
    }
  }, [existingCart, existingCartId]);

  // Helper to open cart modal via search params (keeps current page visible behind)
  // Using window.history.pushState to avoid TanStack Router's navigation lifecycle
  // which would trigger scroll-to-top and other beforeLoad effects
  const openCartModal = useCallback((cartId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('cart', cartId);
    window.history.pushState({}, '', url.toString());
    // Dispatch a custom event that GlobalCartModal listens for
    window.dispatchEvent(new CustomEvent('cart-modal-open', { detail: { cartId } }));
  }, []);

  const addToCart = async ({
    skuId,
    productId,
    onSuccess,
  }: {
    skuId: string;
    productId: string;
    onSuccess?: () => void;
  }) => {
    if (!shopConfiguration) return;

    setIsLoading(true);
    try {
      // Check if we have an existing cart that's not in confirmation step
      if (
        existingCartId &&
        existingCart?.cart &&
        existingCart.cart.currentStepKey !== 'tl-site-confirmation'
      ) {
        await addToCartMutation.mutateAsync({
          skuId,
          productId,
          tenantId: organizationId,
          shopId: shopConfiguration.id,
          cartId: existingCartId,
        });
        onSuccess?.();
        openCartModal(existingCartId);
        return;
      }

      // Remove any existing cart ID from localStorage
      localStorage.removeItem('currentCartId');

      // Create a new cart
      const result = await addToCartMutation.mutateAsync({
        skuId,
        productId,
        tenantId: organizationId,
        shopId: shopConfiguration.id,
      });

      localStorage.setItem('currentCartId', result.id);
      onSuccess?.();
      openCartModal(result.id);
    } catch (error) {
      console.error('Failed to create cart:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    existingCart,
    existingCartId,
    shopConfiguration,
    addToCart,
  };
};

export default useShoppingCart;
